import React, { useState, useEffect } from "react"
import Typography from "aldoo-ra/Typography"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import Button from "aldoo-ra/Button"
import LoadingOverlay from "aldoo-ra/LoadingOverlay"
import { showNotification } from "aldoo-ra/Notification"
import { CreateBackup, ListBackups } from "aldoo-ra/Backups/api"
import { CogIcon, DocumentDuplicateIcon, CircleStackIcon } from "@heroicons/react/24/outline"
import moment from "moment"

const ImportSettingsStep = ({
  fileMode,
  dbMode,
  onFileModeChange,
  onDbModeChange,
  fileModeOptions,
  dbModeOptions,
  manifest,
}) => {
  const [overlayTitle, setOverlayTitle] = useState("")
  const [activityMessage, setActivityMessage] = useState("")
  const [loading, setLoading] = useState(false)
  const [latestBackup, setLatestBackup] = useState(null)
  const [loadingBackups, setLoadingBackups] = useState(true)

  const fetchLatestBackup = async () => {
    setLoadingBackups(true)
    try {
      const response = await ListBackups({ script: "backup-list-local" })
      if (response.result && response.result.files && Array.isArray(response.result.files) && response.result.files.length > 0) {
        const sorted = [...response.result.files].sort((a, b) => new Date(b.timeCreated) - new Date(a.timeCreated))
        setLatestBackup(sorted[0])
      }
    } catch (error) {
      console.error("Failed to fetch backups:", error)
    } finally {
      setLoadingBackups(false)
    }
  }

  useEffect(() => {
    fetchLatestBackup()
  }, [])

  const handleBackup = async () => {
    setOverlayTitle("Creating backup")
    setActivityMessage("")
    setLoading(true)

    try {
      await CreateBackup({
        run: { script: "backup-local" },
        onResponse: (response) => {
          setActivityMessage(response.activity)
        },
      })

      showNotification({
        message: "Backup completed successfully",
        className: "bg-admin_success text-white",
      })

      await fetchLatestBackup()
    } catch (error) {
      showNotification({
        message: "Backup failed: " + (error.message || "Unknown error"),
        className: "bg-admin_error text-white",
      })
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return ""
    const date = new Date(dateString)
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  return (
    <div className="space-y-6">
      {/* Database Update Mode */}
      <div className="bg-admin_canvas rounded-2xl p-6 border border-admin_bordered">
        <div className="flex items-center gap-2 mb-4">
          <CircleStackIcon className="w-5 h-5 text-admin_primary" />

          <Typography variant="admin_body_accent">
            Database Update Mode
          </Typography>
        </div>

        <DropdownSelect
          variant="admin"
          options={dbModeOptions}
          value={dbMode}
          onChange={(selected) => onDbModeChange(selected[0])}
          className="w-full"
        />

        <div className="mt-3 p-4 bg-admin_primary/5 rounded-lg border border-admin_primary/10">
          <Typography variant="admin_body_dim">
            {dbMode?.description || "Select a database update mode"}
          </Typography>
        </div>

        {/* Database Content Info */}
        {manifest?.refs && Object.entries(manifest.refs).length > 0 && (
          <div className="bg-admin_paper rounded-lg border border-admin_bordered p-3 mt-4">
            <Typography variant="admin_txt1_dim">
              This package contains{" "}
              <span className="text-admin_text font-semibold">
                {Object.values(manifest.refs).reduce((sum, count) => sum + count, 0)} database records
              </span>
              {" "}across{" "}
              <span className="text-admin_text font-semibold">
                {Object.keys(manifest.refs).length} collections
              </span>
            </Typography>
          </div>
        )}
      </div>

      {/* File Update Mode */}
      <div className="bg-admin_canvas rounded-2xl p-6 border border-admin_bordered">
        <div className="flex items-center gap-2 mb-4">
          <DocumentDuplicateIcon className="w-5 h-5 text-admin_primary" />

          <Typography variant="admin_body_accent">
            File Update Mode
          </Typography>
        </div>

        <DropdownSelect
          variant="admin"
          options={fileModeOptions}
          value={fileMode}
          onChange={(selected) => onFileModeChange(selected[0])}
          className="w-full"
        />

        <div className="mt-3 p-4 bg-admin_primary/5 rounded-lg border border-admin_primary/10">
          <Typography variant="admin_body_dim">
            {fileMode?.description || "Select a file update mode"}
          </Typography>
        </div>

        {/* File Count Info */}
        {manifest?.files && Object.entries(manifest.files).length > 0 && (
          <div className="mt-4 p-3 bg-admin_paper rounded-lg border border-admin_bordered">
            <Typography variant="admin_txt1_dim">
              This package contains{" "}
              <span className="text-admin_text font-semibold">
                {Object.values(manifest.files).reduce((sum, count) => sum + count, 0)} files
              </span>
              {" "}across{" "}
              <span className="text-admin_text font-semibold">
                {Object.keys(manifest.files).length} directories
              </span>
            </Typography>
          </div>
        )}
      </div>

      {/* Warning Section */}
      <div className="bg-admin_warning/10 border border-admin_warning/30 rounded-xl p-4" style={{ marginBottom: '200px' }}>
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            <svg className="w-5 h-5 text-admin_warning" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>

          <div className="flex-1">
            <Typography 
              variant="admin_body_accent" 
              className="text-admin_warning mb-1"
            >
              Important
            </Typography>

            <Typography 
              variant="admin_body" 
              className="text-admin_warning"
            >
              Always create a backup before importing updates to production environments.
            </Typography>
          </div>
        </div>

        {/* Latest Backup Info and Button */}
        <div className="flex items-start justify-between mt-4 pt-4 border-t border-admin_warning/20">
          <div className="flex-1 min-w-0">
            {loadingBackups ? (
              <Typography 
                variant="admin_txt1" 
                className="text-admin_warning"
              >
                Loading backup info...
              </Typography>
            ) : latestBackup ? (
              <div className="flex flex-col gap-1">
                <div className="text-yellow-700 text-sm">
                  Latest backup:
                </div>

                <div className="text-admin_warning font-semibold text-sm break-all">
                  {latestBackup.path}
                </div>

                <div className="text-admin_warning text-xs">
                  {formatDate(latestBackup.timeCreated)} ({moment(latestBackup.timeCreated).fromNow()})
                </div>
              </div>
            ) : (
              <Typography 
                variant="admin_txt1" 
                className="text-admin_warning"
              >
                No backups found
              </Typography>
            )}
          </div>

          <Button
            variant="admin_secondary"
            onClick={handleBackup}
            className="flex-shrink-0 ml-4"
          >
            Backup Now
          </Button>
        </div>
      </div>

      {/* Loading Overlay for Backup */}
      <LoadingOverlay
        title={overlayTitle}
        loading={loading}
        message={activityMessage}
      />
    </div>
  )
}

export default ImportSettingsStep
